import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";
import { toast } from "sonner";

const empty = {
  key: "",
  title_tr: "",
  title_fr: "",
  description_tr: "",
  description_fr: "",
  image_url: "",
  order: 0,
};

const ServicesManager = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/services");
      setItems(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, editing);
      } else {
        await api.post(`/admin/services`, editing);
      }
      toast.success("✓");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((cur) => ({ ...cur, image_url: res.data.image_url }));
      toast.success("Görsel yüklendi ✓");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadImage(f);
    e.target.value = "";
  };


  const labelCls = "text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-2 block";
  const inputCls =
    "w-full bg-transparent border-b border-white/20 rounded-none px-0 py-2 focus:border-[#D4AF37] focus:outline-none text-white text-sm";

  return (
    <div className="space-y-4" data-testid="services-manager">
      <div className="flex justify-end">
        <button
          onClick={() => setEditing({ ...empty })}
          data-testid="services-add"
          className="bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#C5A059] px-5 py-3 uppercase tracking-[0.2em] text-xs font-bold flex items-center gap-2"
        >
          <Plus size={14} /> {t("admin.dashboard.services.add")}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((svc) => (
          <div
            key={svc.id}
            className="bg-[#121217] border border-white/10 overflow-hidden"
            data-testid={`service-card-admin-${svc.key}`}
          >
            <div className="aspect-video overflow-hidden">
              <img src={svc.image_url} alt={svc.title_tr} className="w-full h-full object-cover" />
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                {svc.key}
              </div>
              <div className="text-white text-sm">{svc.title_tr}</div>
              <div className="text-white/40 text-xs italic">{svc.title_fr}</div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setEditing(svc)}
                  data-testid={`service-edit-${svc.key}`}
                  className="flex-1 border border-white/20 text-white hover:border-[#D4AF37]/60 px-3 py-2 text-xs flex items-center justify-center gap-2"
                >
                  <Pencil size={11} /> {t("admin.dashboard.services.edit")}
                </button>
                <button
                  onClick={() => remove(svc.id)}
                  data-testid={`service-delete-${svc.key}`}
                  className="border border-red-900/50 text-red-400 hover:bg-red-900/20 px-3 py-2 text-xs"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-[#050506]/90 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="service-editor-modal"
        >
          <div className="bg-[#121217] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="font-display text-2xl text-white">
                {editing.id ? "Edit" : "New"} Service
              </div>
              <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelCls}>Key</label>
                <input
                  className={inputCls}
                  value={editing.key}
                  onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Title TR</label>
                  <input
                    className={inputCls}
                    value={editing.title_tr}
                    onChange={(e) => setEditing({ ...editing, title_tr: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Title FR</label>
                  <input
                    className={inputCls}
                    value={editing.title_fr}
                    onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Description TR</label>
                <textarea
                  rows={3}
                  className={inputCls + " resize-none"}
                  value={editing.description_tr}
                  onChange={(e) => setEditing({ ...editing, description_tr: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Description FR</label>
                <textarea
                  rows={3}
                  className={inputCls + " resize-none"}
                  value={editing.description_fr}
                  onChange={(e) => setEditing({ ...editing, description_fr: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Görsel</label>
                {editing.image_url && (
                  <div className="mb-3 relative inline-block">
                    <img
                      src={editing.image_url}
                      alt="preview"
                      className="h-32 w-auto object-cover border border-white/10"
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                  <input
                    className={inputCls + " flex-1"}
                    placeholder="https://... veya yükleyin"
                    value={editing.image_url}
                    onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                    data-testid="service-image-url-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="service-upload-button"
                    className="bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#C5A059] disabled:opacity-50 px-5 py-2 uppercase tracking-[0.2em] text-xs font-bold flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <Upload size={13} />
                    {uploading ? "Yükleniyor…" : "Cihazdan Yükle"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFilePicked}
                    className="hidden"
                    data-testid="service-file-input"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>Order</label>
                <input
                  type="number"
                  className={inputCls}
                  value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "0") })}
                />
              </div>
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  onClick={save}
                  data-testid="service-save"
                  className="bg-[#D4AF37] text-[#0A0A0C] hover:bg-[#C5A059] px-8 py-3 uppercase tracking-[0.2em] text-xs font-bold"
                >
                  {t("admin.dashboard.services.save")}
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="border border-white/20 text-white hover:border-white/40 px-8 py-3 uppercase tracking-[0.2em] text-xs"
                >
                  {t("admin.dashboard.services.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
